import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Zap, User, Mail, Package, CalendarClock, Info, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /account — profile + subscription + credit-policy summary.
 *
 * All credit-policy fields (plan, billing_interval, subscription_status,
 * next_credit_reset_at, credits_roll_over) come from GET /api/auth/me and
 * are refreshed by the AuthProvider. The backend already applies any
 * due monthly reset for paid users on every /auth/me call, so the numbers
 * shown here are always current.
 */
export default function Account() {
  const { user, refresh } = useAuth();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [billingEnabled, setBillingEnabled] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [showCancelledNotice, setShowCancelledNotice] = useState(false);
  const [creditPacks, setCreditPacks] = useState([]);
  const [packBusy, setPackBusy] = useState("");
  const [showPackSuccess, setShowPackSuccess] = useState(false);

  useEffect(() => {
    axios.get(`${API}/billing/status`)
      .then((r) => setBillingEnabled(Boolean(r?.data?.enabled)))
      .catch(() => setBillingEnabled(false));
    axios.get(`${API}/billing/credit-packs`)
      .then((r) => setCreditPacks(r?.data?.packs || []))
      .catch(() => setCreditPacks([]));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("billing") === "cancelled") {
      setShowCancelledNotice(true);
      refresh?.();
      const tm = setTimeout(() => refresh?.(), 3000);
      return () => clearTimeout(tm);
    }
    if (params.get("credits") === "success") {
      setShowPackSuccess(true);
      refresh?.();
      const t1 = setTimeout(() => refresh?.(), 2000);
      const t2 = setTimeout(() => refresh?.(), 6000);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [location.search, refresh]);

  async function buyCreditPack(packId) {
    if (!billingEnabled || packBusy) return;
    setPackBusy(packId);
    try {
      const token = localStorage.getItem("pf_token");
      const res = await axios.post(
        `${API}/billing/credits/checkout`,
        { pack: packId, origin: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else if (res.data?.configured === false) {
        alert(res.data.message || t("account.pack_not_configured"));
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      alert(typeof d === "string" ? d : t("account.pack_checkout_error"));
    } finally {
      setPackBusy("");
    }
  }

  if (!user) return null;

  const isPaid = user.plan === "starter" || user.plan === "pro";
  const nextResetLabel = user.next_credit_reset_at
    ? formatUtcDate(user.next_credit_reset_at, i18n.language)
    : null;

  async function openBillingPortal() {
    if (!billingEnabled || portalBusy) return;
    setPortalBusy(true);
    try {
      const token = localStorage.getItem("pf_token");
      const res = await axios.post(
        `${API}/billing/portal`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res?.data?.url) {
        window.location.href = res.data.url;
      }
    } catch {
      // Fall through — button re-enables and stays inert.
    } finally {
      setPortalBusy(false);
    }
  }

  async function openCancelPortal() {
    if (!billingEnabled || cancelBusy || !isPaid) return;
    setCancelBusy(true);
    try {
      const token = localStorage.getItem("pf_token");
      const res = await axios.post(
        `${API}/billing/cancel-portal`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res?.data?.url) {
        window.location.href = res.data.url;
      }
    } catch {
      // Fall through — button re-enables and stays inert.
    } finally {
      setCancelBusy(false);
    }
  }

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
      <p className="text-xs font-mono text-blue-400 mb-2">{t("account.eyebrow")}</p>
      <h1 className="text-4xl font-semibold tracking-tighter mb-10 bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent">{t("account.title")}</h1>

      <div className="space-y-6">
        <section className="bg-[#09090B]/90 border border-[#1F2937] backdrop-blur-sm rounded-xl p-8">
          <h2 className="text-lg font-medium mb-6">{t("account.profile_title")}</h2>
          <div className="space-y-4">
            <Row icon={User} label={t("account.name_label")} value={user.name} testId="acc-name" />
            <Row icon={Mail} label={t("account.email_label")} value={user.email} testId="acc-email" />
          </div>
        </section>

        <section className="bg-[#09090B]/90 border border-[#1F2937] backdrop-blur-sm rounded-xl p-8">
          <h2 className="text-lg font-medium mb-6">{t("account.subscription_title")}</h2>
          <div className="grid sm:grid-cols-2 gap-6 mb-6">
            <div>
              <p className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
                <Package className="w-3.5 h-3.5" /> {t("account.current_plan")}
              </p>
              <p className="text-2xl font-semibold capitalize" data-testid="acc-plan">
                {user.plan || user.plan_tier || "free"}
              </p>
              {isPaid && (
                <p className="text-xs font-mono text-zinc-500 mt-1" data-testid="acc-billing-interval">
                  {user.billing_interval === "yearly"
                    ? t("account.billed_yearly")
                    : t("account.billed_monthly")}
                </p>
              )}
            </div>
            <div>
              <p className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5" /> {t("account.credits_available")}
              </p>
              <p className="text-2xl font-semibold font-mono" data-testid="acc-credits">
                {user.credits}
              </p>
              {isPaid ? (
                <p className="text-xs font-mono text-zinc-500 mt-1" data-testid="acc-allowance">
                  {t("account.subscription_note", { allowance: user.subscription_credit_allowance })}
                </p>
              ) : (
                <p
                  className="text-xs font-mono text-zinc-500 mt-1"
                  data-testid="acc-free-note"
                >
                  {t("account.free_note")}
                </p>
              )}
            </div>
          </div>

          {isPaid && nextResetLabel && (
            <div
              className="mb-3 flex items-start gap-2 text-xs text-zinc-400 bg-[#030712] border border-[#1F2937] rounded-md px-3 py-2"
              data-testid="acc-next-reset"
            >
              <CalendarClock className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" />
              <span>
                {t("account.next_reset_prefix")}{" "}
                <span className="text-zinc-200 font-mono">{nextResetLabel}</span>
              </span>
            </div>
          )}

          {showCancelledNotice && (
            <div
              className="mb-3 flex items-start gap-2 text-xs text-blue-300 bg-blue-500/10 border border-blue-500/30 rounded-md px-3 py-2"
              data-testid="acc-cancelled-notice"
            >
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>
                {t("account.cancelled_notice")}
              </span>
            </div>
          )}

          {isPaid && user.cancel_at_period_end && user.subscription_access_ends_at && (
            <div
              className="mb-6 flex items-start gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2"
              data-testid="acc-cancel-scheduled"
            >
              <CalendarClock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>
                {t("account.cancel_scheduled_prefix")}{" "}
                <span className="text-amber-200 font-mono">
                  {formatUtcDate(user.subscription_access_ends_at, i18n.language)}
                </span>
                {t("account.cancel_scheduled_suffix")}
              </span>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <Link to="/pricing" data-testid="acc-upgrade-btn">
              <Button className="bg-blue-600 hover:bg-blue-500 text-white">
                {user.plan === "free" ? t("account.upgrade_paid_plan") : t("account.change_plan_credits")}
              </Button>
            </Link>
            {billingEnabled && isPaid && (
              <Button
                variant="outline"
                className="border-[#2D313F] bg-[#030712] text-zinc-200 hover:bg-[#1F2937]"
                onClick={openBillingPortal}
                disabled={portalBusy}
                data-testid="acc-manage-billing-btn"
              >
                {portalBusy ? t("account.opening") : t("account.manage_billing")}
              </Button>
            )}
            {billingEnabled && isPaid && !user.cancel_at_period_end && (
              <Button
                variant="outline"
                className="border-red-900/60 bg-[#030712] text-red-300 hover:bg-red-950/40 hover:text-red-200"
                onClick={openCancelPortal}
                disabled={cancelBusy}
                data-testid="acc-cancel-subscription-btn"
              >
                {cancelBusy ? t("account.opening") : t("account.cancel_subscription")}
              </Button>
            )}
          </div>
        </section>

        <section
          className="bg-[#09090B]/90 border border-[#1F2937] backdrop-blur-sm rounded-xl p-8"
          data-testid="acc-credit-packs"
        >
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <h2 className="text-lg font-medium">{t("account.buy_extra_credits")}</h2>
          </div>
          <p className="text-sm text-zinc-400 mb-1">
            {t("account.buy_extra_desc")}
          </p>
          <p className="text-xs font-mono text-zinc-500 mb-6">
            {t("account.buy_extra_note")}
          </p>

          {showPackSuccess && (
            <div
              className="mb-4 flex items-start gap-2 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-md px-3 py-2"
              data-testid="acc-credit-pack-success"
            >
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>{t("account.credit_pack_success")}</span>
            </div>
          )}

          {!billingEnabled ? (
            <p className="text-sm text-zinc-500">{t("account.credit_packs_unavailable")}</p>
          ) : creditPacks.length === 0 ? (
            <p className="text-sm text-zinc-500">{t("account.credit_packs_loading")}</p>
          ) : (
            <div className="grid sm:grid-cols-3 gap-4">
              {creditPacks.map((p) => (
                <div
                  key={p.pack_id}
                  data-testid={`credit-pack-${p.pack_id}`}
                  className="bg-[#030712] border border-[#1F2937] rounded-lg p-5 flex flex-col"
                >
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-xl font-semibold font-mono">{p.credits}</span>
                    <span className="text-xs text-zinc-500 font-mono">{t("account.credits_word")}</span>
                  </div>
                  <div className="text-2xl font-semibold mb-1">
                    €{p.display_price_eur}
                    <span className="text-xs text-zinc-500 font-mono ml-1">{t("glossary.net")}</span>
                  </div>
                  <p className="text-xs text-zinc-500 mb-4">{t("account.taxes_note")}</p>
                  <Button
                    data-testid={`credit-pack-buy-${p.pack_id}`}
                    onClick={() => buyCreditPack(p.pack_id)}
                    disabled={!p.available || packBusy === p.pack_id}
                    className="mt-auto bg-emerald-600 hover:bg-emerald-500 text-white"
                  >
                    {packBusy === p.pack_id
                      ? t("account.loading")
                      : p.available
                        ? t("account.buy_pack")
                        : t("account.coming_soon")}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {user.plan === "free" && (
            <p className="text-xs font-mono text-zinc-500 mt-4">
              {t("account.upgrade_tip_prefix")}{" "}
              <Link to="/pricing" className="text-blue-400 hover:underline">{t("account.upgrade_tip_link")}</Link>{" "}
              {t("account.upgrade_tip_suffix")}
            </p>
          )}
        </section>

        <section className="bg-[#09090B]/90 border border-[#1F2937] backdrop-blur-sm rounded-xl p-8">
          <h2 className="text-lg font-medium mb-2">{t("account.security_data")}</h2>
          <p className="text-sm text-zinc-400 mb-1">{t("account.security_note")}</p>
          <p className="text-xs text-zinc-600 font-mono mt-4">
            {t("account.signed_in_via")} {user.google_connected ? t("account.provider_google") : t("account.provider_email")}
          </p>
        </section>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="w-4 h-4 text-zinc-500" />
      <div className="flex-1">
        <p className="text-xs font-mono uppercase tracking-wider text-zinc-500">{label}</p>
        <p className="text-white" data-testid={testId}>{value}</p>
      </div>
    </div>
  );
}

// Render a UTC ISO timestamp in the user's local calendar day, without
// leaking hours/minutes — a subscription reset is a "when it lands" date.
function formatUtcDate(iso, lang) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang || undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}
